import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar, AvatarGroup, Badge, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, InputAdornment, MenuItem, Popover, Select,
  Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseIcon from '@mui/icons-material/Close'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined'
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import DoneIcon from '@mui/icons-material/Done'
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ChatConversation, ChatMention, ChatMessage, ChatParticipant, TypingUser,
} from '@cmms/types'
import { MentionComposer, MentionText } from './MentionComposer'

/** Maps a tapped chat mention to the appropriate detail route. */
function navigateToMention(navigate: ReturnType<typeof useNavigate>, m: ChatMention) {
  const path: Partial<Record<typeof m.type, string>> = {
    user:       `/people/${m.id}`,
    work_order: `/work-orders/${m.id}`,
    asset:      `/assets/${m.id}`,
    location:   `/locations/${m.id}`,
    vendor:     `/vendors/${m.id}`,
    part:       `/inventory/${m.id}`,
    checklist:  `/checklists/${m.id}`,
  }
  const target = path[m.type]
  if (target) navigate(target)
}

import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { ApiError } from '@cmms/api-client'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { DocumentViewer } from '../../components/DocumentViewer'
import { UserAvatar } from '../../components/UserAvatar'
import { useAuthStore } from '../../store/authStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { useChatSocket } from '../../hooks/useChatSocket'

const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '👏', '🔥', '✅', '👀']
const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  { label: 'Smileys', emojis: ['😀','😃','😄','😁','😂','🤣','😊','😇','🙂','😉','😍','🥰','😘','😋','😛','😜','🤪','😎','🤩','🥳','😏','😒','😞','😔','😟','😢','😭','😤','🤯','😱','🥶','🥵','😴','🤮','🤢','🤧','😷'] },
  { label: 'Gestures', emojis: ['👍','👎','👏','🙌','🤝','🤜','🤛','✊','👊','🤞','✌️','🤟','🤙','👋','🫡','💪','🙏','👆','👇','👈','👉','☝️','🫵'] },
  { label: 'Hearts', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❤️‍🔥','💕','💞','💓','💗','💖','💝','💘'] },
  { label: 'Objects', emojis: ['🔧','🔨','⚙️','🛠️','📋','📌','📎','✏️','📝','📦','🏷️','🔑','🔒','💡','🔔','📢','⏰','📅','💼','🗂️','📊','📈','✅','❌','⚠️','🚧'] },
  { label: 'Symbols', emojis: ['⭐','🌟','💥','🔥','✨','💯','🎯','🏆','🥇','🎉','🎊','🚀','💰','⚡','♻️','🔄','➡️','⬅️','⬆️','⬇️','🆗','🆕','🔴','🟢','🟡','🔵'] },
]
const AVATAR_COLORS = ['#0D9488', '#D97706', '#2563EB', '#DC2626', '#7C3AED', '#EA580C', '#059669', '#DB2777', '#0E7490', '#65A30D']

function userColor(id: string) {
  let hash = 0
  for (const c of id) hash = ((hash << 5) - hash + c.charCodeAt(0)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!
}

function initials(name: string) {
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
}

export function ChatsPage() {
  const user = useAuthStore((s) => s.user)
  const myId = user?.id ?? ''
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { formatDateShort } = useOrgSettings()
  const { conversationId: urlConvId } = useParams<{ conversationId?: string }>()

  const [activeId, setActiveId] = useState<string | null>(urlConvId ?? null)
  useEffect(() => { if (urlConvId) setActiveId(urlConvId) }, [urlConvId])

  function handleSelectConv(id: string) {
    setActiveId(id)
    navigate(`/chats/${id}`, { replace: true })
    api.chat.markRead(id)
  }
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const convsQ = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: () => api.chat.listConversations(),
  })
  const conversations: ChatConversation[] = Array.isArray(convsQ.data) ? convsQ.data : []

  const filtered = useMemo(() => {
    if (!search.trim()) return conversations
    const q = search.toLowerCase()
    return conversations.filter((c) => {
      const name = c.type === 'direct'
        ? c.participants.find((p) => p.user_id !== myId)?.name ?? ''
        : c.name ?? ''
      return name.toLowerCase().includes(q)
    })
  }, [conversations, search, myId])

  if (!activeId && filtered.length > 0) {
    const first = filtered[0]!.id
    setActiveId(first)
    navigate(`/chats/${first}`, { replace: true })
  }
  const active = activeId ? conversations.find((c) => c.id === activeId) ?? null : null

  return (
    <>
      <PageShell title="Chats" scrollContent={false}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: { xs: 3, md: 4 } }}>
          {/* Sidebar */}
          <Box sx={{ width: 320, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField placeholder="Search chats" value={search} onChange={(e) => setSearch(e.target.value)} size="small" fullWidth
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2, '& fieldset': { borderColor: 'divider' } } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }} />
              <Tooltip title="New conversation" arrow>
                <IconButton size="small" onClick={() => setCreateOpen(true)}
                  sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 34, height: 34, '&:hover': { bgcolor: 'primary.dark' } }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
              {convsQ.isLoading ? (
                <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No conversations yet.</Typography>
                </Box>
              ) : (
                filtered.map((c) => {
                  const sel = c.id === activeId
                  const other = c.type === 'direct' ? c.participants.find((p) => p.user_id !== myId) : null
                  const displayName = c.type === 'direct' ? (other?.name ?? 'Unknown') : (c.name ?? 'Group')
                  const lm = c.last_message
                  const lastText = lm?.text ?? (lm?.file_name ? `📎 ${lm.file_name}` : '')
                  const lastTime = c.last_message ? shortTime(new Date(c.last_message.created_at), formatDateShort) : ''
                  return (
                    <Stack key={c.id} direction="row" alignItems="center" spacing={1.5} onClick={() => handleSelectConv(c.id)}
                      sx={{
                        px: 2, py: 1.5, cursor: 'pointer', transition: 'background-color 120ms',
                        bgcolor: sel ? primary.selected : 'transparent',
                        '&:hover': { bgcolor: sel ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                      }}>
                      {c.type === 'team' ? (
                        <Avatar sx={{ width: 40, height: 40, bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)', color: 'text.secondary' }}>
                          <GroupsOutlinedIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                      ) : (
                        <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot"
                          sx={{ '& .MuiBadge-badge': { bgcolor: other?.online ? '#059669' : '#94A3B8', border: '2px solid', borderColor: 'background.paper', width: 8, height: 8, borderRadius: 99,
                            ...(other?.online ? { animation: 'pulse 2s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } } } : {}) } }}>
                          <UserAvatar name={other?.name} avatarUrl={other?.avatar_url} size={40} sx={{ bgcolor: userColor(other?.user_id ?? '') }} />
                        </Badge>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: 13.5, fontWeight: c.unread_count > 0 ? 700 : 500 }} noWrap>{displayName}</Typography>
                          <Typography sx={{ fontSize: 10.5, color: c.unread_count > 0 ? 'primary.main' : 'text.secondary', fontWeight: c.unread_count > 0 ? 700 : 400, flexShrink: 0 }}>{lastTime}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.15 }}>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{lastText}</Typography>
                          {c.unread_count > 0 && (
                            <Box sx={{ minWidth: 18, height: 18, borderRadius: 99, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 1, flexShrink: 0 }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'primary.contrastText' }}>{c.unread_count}</Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  )
                })
              )}
            </Box>
          </Box>

          {/* Chat area */}
          {active ? <ChatThread convo={active} myId={myId} /> : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Select a conversation</Typography>
            </Box>
          )}
        </Box>
      </PageShell>

      {createOpen && <NewConversationDialog onClose={() => setCreateOpen(false)} myId={myId} onCreated={(id) => { handleSelectConv(id); qc.invalidateQueries({ queryKey: ['chat-conversations'] }) }} />}
    </>
  )
}

// ─── Chat thread ───────────────────────────────────────────────────────────

function ChatThread({ convo, myId }: { convo: ChatConversation; myId: string }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { formatTime, formatDateShort } = useOrgSettings()
  const { sendTyping: wsSendTyping, subscribe } = useChatSocket()
  const confirmDlg = useConfirm()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState('')
  // Mentions tracked alongside the draft. The composer maintains both —
  // we forward `mentions` to the backend on send so the renderer can
  // turn them back into chips and tap-to-navigate works.
  const [draftMentions, setDraftMentions] = useState<ChatMention[]>([])
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null)
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([])
  const typingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const [infoOpen, setInfoOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null)
  const [recording, setRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingChunks = useRef<Blob[]>([])
  const recordingTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  const other = convo.type === 'direct' ? convo.participants.find((p) => p.user_id !== myId) : null
  const displayName = convo.type === 'direct' ? (other?.name ?? 'Unknown') : (convo.name ?? 'Group')

  const messagesQ = useQuery({
    queryKey: ['chat-messages', convo.id],
    queryFn: () => api.chat.getMessages(convo.id, { limit: 50 }),
  })
  const messages: ChatMessage[] = messagesQ.data?.data ?? []

  // Listen for WebSocket events for this conversation
  useEffect(() => {
    return subscribe((event) => {
      if (event.conversation_id !== convo.id) return
      if (event.type === 'typing' && event.user_id !== myId) {
        const userId = event.user_id as string
        const name = (event.name as string) ?? ''
        setTypingUsers((prev) => {
          if (prev.some((t) => t.user_id === userId)) return prev
          return [...prev, { user_id: userId, name }]
        })
        // Auto-clear after 5s
        const existing = typingTimers.current.get(userId)
        if (existing) clearTimeout(existing)
        typingTimers.current.set(userId, setTimeout(() => {
          setTypingUsers((prev) => prev.filter((t) => t.user_id !== userId))
          typingTimers.current.delete(userId)
        }, 5000))
      }
      if (event.type === 'new_message') {
        qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] })
        // Clear typing for the sender
        const senderId = event.message?.sender_id
        if (senderId) setTypingUsers((prev) => prev.filter((t) => t.user_id !== senderId))
        setTimeout(scrollToBottom, 150)
      }
    })
  }, [convo.id, myId, subscribe, qc])

  const sendM = useMutation({
    mutationFn: () => api.chat.sendMessage(convo.id, {
      text: draft.trim(),
      reply_to_id: replyTo?.id,
      mentions: draftMentions,
    }),
    onSuccess: () => {
      setDraft('')
      setDraftMentions([])
      setReplyTo(null)
      qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] })
      qc.invalidateQueries({ queryKey: ['chat-conversations'] })
      setTimeout(scrollToBottom, 100)
    },
  })

  const uploadM = useMutation({
    mutationFn: (file: File) => api.chat.uploadFile(convo.id, file, draft.trim() || undefined),
    onSuccess: () => {
      setDraft('')
      setPendingFile(null)
      qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] })
      qc.invalidateQueries({ queryKey: ['chat-conversations'] })
      setTimeout(scrollToBottom, 100)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to upload file'),
  })

  const addReactM = useMutation({
    mutationFn: ({ msgId, emoji }: { msgId: string; emoji: string }) => api.chat.addReaction(convo.id, msgId, { emoji }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] }),
  })

  const removeReactM = useMutation({
    mutationFn: ({ msgId, emoji }: { msgId: string; emoji: string }) => api.chat.removeReaction(convo.id, msgId, emoji),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] }),
  })

  function toggleReaction(msg: ChatMessage, emoji: string) {
    const existing = (msg.reactions ?? []).find((r) => r.emoji === emoji)
    if (existing && existing.user_ids.includes(myId)) {
      removeReactM.mutate({ msgId: msg.id, emoji })
    } else {
      addReactM.mutate({ msgId: msg.id, emoji })
    }
  }

  const pinM = useMutation({
    mutationFn: ({ msgId, pin }: { msgId: string; pin: boolean }) => pin ? api.chat.pinMessage(convo.id, msgId) : api.chat.unpinMessage(convo.id, msgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] }),
  })

  const deleteMsgM = useMutation({
    mutationFn: (msgId: string) => api.chat.deleteMessage(convo.id, msgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] })
      qc.invalidateQueries({ queryKey: ['chat-conversations'] })
      toast.success('Message deleted')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  const editMsgM = useMutation({
    mutationFn: ({ msgId, text }: { msgId: string; text: string }) => api.chat.editMessage(convo.id, msgId, { text }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] })
      setEditingMsg(null)
      setEditText('')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to edit'),
  })

  const [editingMsg, setEditingMsg] = useState<ChatMessage | null>(null)
  const [editText, setEditText] = useState('')
  const [viewingFile, setViewingFile] = useState<ChatMessage | null>(null)

  async function handleClear() {
    const ok = await confirmDlg({ title: 'Clear all messages?', message: 'All messages in this conversation will be deleted. This cannot be undone.', confirmLabel: 'Clear', variant: 'danger' })
    if (ok) {
      await api.chat.clearMessages(convo.id)
      qc.invalidateQueries({ queryKey: ['chat-messages', convo.id] })
      toast.success('Messages cleared')
    }
  }

  async function handleDeleteConvo() {
    const ok = await confirmDlg({ title: 'Delete conversation?', message: 'You will leave this conversation and it will be removed from your list.', confirmLabel: 'Delete', variant: 'danger' })
    if (ok) {
      await api.chat.deleteConversation(convo.id)
      qc.invalidateQueries({ queryKey: ['chat-conversations'] })
      navigate('/chats', { replace: true })
      toast.success('Conversation deleted')
    }
  }

  useEffect(() => { scrollToBottom() }, [convo.id])

  function scrollToBottom() {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }

  function handleScroll() {
    const el = scrollRef.current
    if (!el) return
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 100)
  }

  function handleSend() {
    if (editingMsg) {
      if (!editText.trim()) return
      editMsgM.mutate({ msgId: editingMsg.id, text: editText.trim() })
      return
    }
    if (pendingFile) {
      uploadM.mutate(pendingFile)
      return
    }
    if (!draft.trim()) return
    sendM.mutate()
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  function insertEmoji(emoji: string) {
    if (editingMsg) {
      setEditText((t) => t + emoji)
    } else {
      setDraft((t) => t + emoji)
    }
    setEmojiAnchor(null)
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' })
      recordingChunks.current = []
      mr.ondataavailable = (e) => { if (e.data.size > 0) recordingChunks.current.push(e.data) }
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        if (recordingTimer.current) { clearInterval(recordingTimer.current); recordingTimer.current = null }
        const blob = new Blob(recordingChunks.current, { type: mr.mimeType })
        const ext = mr.mimeType.includes('webm') ? 'webm' : 'm4a'
        const file = new File([blob], `voice-message.${ext}`, { type: mr.mimeType })
        setPendingFile(file)
        setRecording(false)
        setRecordingTime(0)
      }
      mr.start()
      mediaRecorderRef.current = mr
      setRecording(true)
      setRecordingTime(0)
      recordingTimer.current = setInterval(() => setRecordingTime((t) => t + 1), 1000)
    } catch {
      toast.error('Could not access microphone. Check browser permissions.')
    }
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop()
    mediaRecorderRef.current = null
  }

  function cancelRecording() {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null
      mediaRecorderRef.current.onstop = null
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop())
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current = null
    }
    if (recordingTimer.current) { clearInterval(recordingTimer.current); recordingTimer.current = null }
    recordingChunks.current = []
    setRecording(false)
    setRecordingTime(0)
  }

  // Send typing indicator via WebSocket
  useEffect(() => {
    if (draft.trim()) wsSendTyping(convo.id)
  }, [draft, convo.id, wsSendTyping])

  const grouped = useMemo(() => {
    const groups: { label: string; messages: ChatMessage[] }[] = []
    let currentLabel = ''
    messages.forEach((m) => {
      const label = dayLabel(new Date(m.created_at))
      if (label !== currentLabel) { groups.push({ label, messages: [] }); currentLabel = label }
      groups[groups.length - 1]!.messages.push(m)
    })
    return groups
  }, [messages])

  const isTyping = typingUsers.length > 0

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {convo.type === 'team' ? (
          <Avatar sx={{ width: 40, height: 40, bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)', color: 'text.secondary' }}>
            <GroupsOutlinedIcon sx={{ fontSize: 20 }} />
          </Avatar>
        ) : (
          <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot"
            sx={{ '& .MuiBadge-badge': { bgcolor: other?.online ? '#059669' : '#94A3B8', border: '2px solid', borderColor: 'background.paper', width: 8, height: 8, borderRadius: 99 } }}>
            <UserAvatar name={other?.name} avatarUrl={other?.avatar_url} size={40} sx={{ bgcolor: userColor(other?.user_id ?? '') }} />
          </Badge>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{displayName}</Typography>
          <Typography sx={{ fontSize: 12, color: isTyping ? 'primary.main' : 'text.secondary', fontWeight: isTyping ? 600 : 400 }}>
            {isTyping
              ? `${typingUsers.map((t) => t.name.split(' ')[0]).join(', ')} typing…`
              : convo.type === 'team' ? `${convo.participants.length} members` : other?.online ? 'Online' : 'Offline'}
          </Typography>
        </Box>
        {convo.type === 'team' && (
          <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 11, fontWeight: 600, border: '2px solid', borderColor: 'background.paper' } }}>
            {convo.participants.slice(0, 4).map((p) => (
              <Tooltip key={p.user_id} title={p.name} arrow><UserAvatar name={p.name} avatarUrl={p.avatar_url} size={28} sx={{ bgcolor: userColor(p.user_id) }} /></Tooltip>
            ))}
          </AvatarGroup>
        )}
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Conversation info" arrow><IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ color: 'text.secondary' }}><InfoOutlinedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Clear messages" arrow><IconButton size="small" onClick={handleClear} sx={{ color: 'text.secondary' }}><DeleteSweepOutlinedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Delete conversation" arrow><IconButton size="small" onClick={handleDeleteConvo} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        </Stack>
      </Stack>

      {/* Messages */}
      <Box ref={scrollRef} onScroll={handleScroll} sx={{
        flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 3, py: 2, position: 'relative',
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' },
      }}>
        {messagesQ.isLoading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : (
          grouped.map((group) => (
            <Box key={group.label}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 2.5 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>{group.label}</Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>
              {group.messages.map((m, i) => {
                if (m.is_system) return (
                  <Typography key={m.id} sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', py: 1, fontStyle: 'italic' }}>{m.text}</Typography>
                )
                const isMe = m.sender_id === myId
                const showAvatar = !isMe && (i === 0 || group.messages[i - 1]?.sender_id !== m.sender_id || !!group.messages[i - 1]?.is_system)
                return (
                  <MessageBubble key={m.id} message={m} isMe={isMe} showAvatar={showAvatar} myId={myId}
                    participants={convo.participants}
                    onReact={(emoji) => toggleReaction(m, emoji)}
                    onReply={() => setReplyTo(m)}
                    onPin={() => pinM.mutate({ msgId: m.id, pin: !m.pinned })}
                    onDelete={() => deleteMsgM.mutate(m.id)}
                    onEdit={() => { setEditingMsg(m); setEditText(m.text ?? '') }}
                    onViewFile={() => setViewingFile(m)} />
                )
              })}
            </Box>
          ))
        )}

        {/* Typing indicator */}
        {isTyping && (
          <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mt: 1 }}>
            <UserAvatar
              name={typingUsers[0]?.name}
              avatarUrl={convo.participants.find((p) => p.user_id === typingUsers[0]?.user_id)?.avatar_url}
              size={32} sx={{ bgcolor: userColor(typingUsers[0]?.user_id ?? '') }}
            />
            <Box sx={{ px: 2, py: 1.25, borderRadius: '16px 16px 16px 4px', bgcolor: (t) => t.palette.mode === 'light' ? '#F0F0F2' : 'rgba(255,255,255,0.08)' }}>
              <Stack direction="row" spacing={0.4} alignItems="center">
                {[0, 1, 2].map((i) => (
                  <motion.div key={i} animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: 'text.secondary' }} />
                  </motion.div>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Scroll to bottom */}
      <AnimatePresence>
        {showScrollBtn && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
            style={{ position: 'absolute', bottom: 90, right: 32, zIndex: 5 }}>
            <IconButton onClick={scrollToBottom}
              sx={{ bgcolor: 'background.paper', color: 'text.secondary', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 12px -4px rgba(0,0,0,0.2)', width: 36, height: 36, '&:hover': { bgcolor: 'background.paper', color: 'primary.main' } }}>
              <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reply preview */}
      {replyTo && (
        <Box sx={{ px: 3, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 1.5, py: 0.75, borderLeft: '3px solid', borderColor: 'primary.main', bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)', borderRadius: '0 8px 8px 0', pr: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main' }}>{replyTo.sender_name ?? 'Unknown'}</Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>{replyTo.text}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ color: 'text.secondary' }}>
              <Typography sx={{ fontSize: 14, lineHeight: 1 }}>×</Typography>
            </IconButton>
          </Stack>
        </Box>
      )}

      {/* Edit message bar */}
      {editingMsg && (
        <Box sx={{ px: 3, pt: 1.5, borderTop: replyTo ? 'none' : '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
            pl: 1.5, py: 0.75, borderLeft: '3px solid', borderColor: '#D97706',
            bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(217,119,6,0.04)' : 'rgba(217,119,6,0.08)',
            borderRadius: '0 8px 8px 0', pr: 1,
          }}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#D97706' }}>Editing message</Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>{editingMsg.text}</Typography>
            </Box>
            <IconButton size="small" onClick={() => { setEditingMsg(null); setEditText('') }} sx={{ color: 'text.secondary' }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        </Box>
      )}

      {/* Pending file preview */}
      {pendingFile && (
        <Box sx={{ px: 3, pt: 1.5, pb: 0, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{
            px: 2, py: 1.25, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'light' ? '#F5F5F7' : 'rgba(255,255,255,0.04)',
            border: '1px solid', borderColor: 'divider',
          }}>
            {pendingFile.type.startsWith('image/') ? (
              <Box component="img" src={URL.createObjectURL(pendingFile)}
                sx={{ width: 48, height: 48, borderRadius: 1.5, objectFit: 'cover' }} />
            ) : (
              <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AttachFileOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{pendingFile.name}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{formatFileSize(pendingFile.size)}</Typography>
            </Box>
            <IconButton size="small" onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
              sx={{ color: 'text.secondary' }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>
      )}

      {/* Composer */}
      <Box sx={{ px: 3, py: 2, borderTop: replyTo || pendingFile ? 'none' : '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <input ref={fileInputRef} type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingFile(f) }}
          hidden />
        <Stack direction="row" alignItems="center"
          sx={{
            borderRadius: 3, bgcolor: (t) => t.palette.mode === 'light' ? '#F5F5F7' : 'rgba(255,255,255,0.04)',
            border: '1px solid', borderColor: 'divider', pl: 1, pr: 1, py: 0.5,
            transition: 'border-color 160ms ease',
            '&:focus-within': { borderColor: 'primary.main' },
          }}>
          <Tooltip title="Attach file" arrow>
            <IconButton size="small" onClick={() => fileInputRef.current?.click()}
              sx={{ color: 'text.secondary' }}><AttachFileOutlinedIcon sx={{ fontSize: 20 }} /></IconButton>
          </Tooltip>
          {editingMsg ? (
            // Edit mode: plain TextField — editing existing messages
            // doesn't currently support changing mentions in place.
            <TextField
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder={'Edit message…'}
              size="small" fullWidth multiline maxRows={4} onKeyDown={handleKeyDown}
              sx={{ mx: 0.5, '& .MuiOutlinedInput-root': { fontSize: 14, bgcolor: 'transparent', '& fieldset': { border: 'none' } }, '& .MuiOutlinedInput-input': { py: 1 } }} />
          ) : (
            <Box sx={{ flex: 1, mx: 0.5 }}>
              <MentionComposer
                value={draft}
                mentions={draftMentions}
                onChange={(text, mentions) => { setDraft(text); setDraftMentions(mentions) }}
                onSubmit={handleSend}
                placeholder={pendingFile ? 'Add a caption (optional)…' : 'Type a message — @ to tag people, # for work orders/assets/…'}
              />
            </Box>
          )}
          <Tooltip title="Emoji" arrow>
            <IconButton size="small" onClick={(e) => setEmojiAnchor(e.currentTarget)}
              sx={{ color: emojiAnchor ? 'primary.main' : 'text.secondary' }}>
              <EmojiEmotionsOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {draft.trim() || pendingFile || editingMsg ? (
            <Tooltip title={editingMsg ? 'Save (Enter)' : 'Send (Enter)'} arrow>
              <IconButton size="small" onClick={handleSend} disabled={sendM.isPending || uploadM.isPending || editMsgM.isPending}
                sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 36, height: 36, ml: 0.5, '&:hover': { bgcolor: 'primary.dark' } }}>
                {(uploadM.isPending || editMsgM.isPending) ? <CircularProgress size={18} sx={{ color: 'primary.contrastText' }} /> : <SendOutlinedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          ) : recording ? (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ ml: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#DC2626', animation: 'pulse 1s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'text.secondary', minWidth: 32 }}>
                {Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}
              </Typography>
              <Tooltip title="Cancel" arrow>
                <IconButton size="small" onClick={cancelRecording} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Stop & attach" arrow>
                <IconButton size="small" onClick={stopRecording}
                  sx={{ bgcolor: '#DC2626', color: '#fff', width: 36, height: 36, '&:hover': { bgcolor: '#B91C1C' } }}>
                  <StopCircleOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Tooltip title="Voice message" arrow>
              <IconButton size="small" onClick={startRecording}
                sx={{ color: 'text.secondary', width: 36, height: 36, ml: 0.5, '&:hover': { color: '#DC2626' } }}>
                <MicNoneOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Emoji picker */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { borderRadius: 2.5, width: 340, maxHeight: 360, overflow: 'hidden', display: 'flex', flexDirection: 'column' } } }}
      >
        <Box sx={{ overflowY: 'auto', px: 2, py: 1.5, scrollbarWidth: 'thin' }}>
          {EMOJI_GROUPS.map((g) => (
            <Box key={g.label} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.75 }}>
                {g.label}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                {g.emojis.map((e) => (
                  <Box key={e} onClick={() => insertEmoji(e)}
                    sx={{
                      width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 1, cursor: 'pointer', fontSize: 20, userSelect: 'none',
                      transition: 'background-color 100ms, transform 100ms',
                      '&:hover': { bgcolor: 'action.hover', transform: 'scale(1.2)' },
                    }}>
                    {e}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>

      {infoOpen && <ChatInfoDrawer convo={convo} myId={myId} messages={messages} onClose={() => setInfoOpen(false)} />}

      {/* Document viewer for file preview */}
      <DocumentViewer
        open={Boolean(viewingFile)}
        doc={viewingFile ? { id: viewingFile.id, file_url: viewingFile.file_url ?? '', file_name: viewingFile.file_name ?? 'File', file_size: viewingFile.file_size ?? 0, content_type: viewingFile.file_type ?? '' } as any : null}
        directUrl={viewingFile?.file_url ?? null}
        fetchBlob={() => api.chat.viewFile(convo.id, viewingFile!.id)}
        onClose={() => setViewingFile(null)}
      />
    </Box>
  )
}

// ─── Chat info drawer (WhatsApp-style) ──────────────────────────────────────

function ChatInfoDrawer({ convo, myId, messages, onClose }: {
  convo: ChatConversation; myId: string; messages: ChatMessage[]; onClose: () => void
}) {
  const { formatDateShort } = useOrgSettings()
  const [infoTab, setInfoTab] = useState<'members' | 'pinned' | 'search'>('members')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searching, setSearching] = useState(false)

  const other = convo.type === 'direct' ? convo.participants.find((p) => p.user_id !== myId) : null
  const displayName = convo.type === 'direct' ? (other?.name ?? 'Unknown') : (convo.name ?? 'Group')
  const pinnedMessages = messages.filter((m) => m.pinned)

  async function handleSearch() {
    if (!searchQuery.trim()) return
    setSearching(true)
    try {
      const results = await api.chat.searchMessages(convo.id, searchQuery.trim())
      setSearchResults(results)
    } catch {} finally { setSearching(false) }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh' } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, pt: 2.5, pb: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Conversation info</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Stack>

      {/* Profile section */}
      <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
        {convo.type === 'team' ? (
          <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 1.5, bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)', color: 'text.secondary' }}>
            <GroupsOutlinedIcon sx={{ fontSize: 34 }} />
          </Avatar>
        ) : (
          <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot"
            sx={{ '& .MuiBadge-badge': { bgcolor: other?.online ? '#059669' : '#94A3B8', border: '3px solid', borderColor: 'background.paper', width: 14, height: 14, borderRadius: 99 }, mx: 'auto', mb: 1.5 }}>
            <UserAvatar name={other?.name} avatarUrl={other?.avatar_url} size={72} sx={{ bgcolor: userColor(other?.user_id ?? '') }} />
          </Badge>
        )}
        <Typography sx={{ fontSize: 18, fontWeight: 700, mt: 0.5 }}>{displayName}</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
          {convo.type === 'team' ? `${convo.participants.length} members · Created ${shortTime(new Date(convo.created_at), formatDateShort)}` : other?.online ? 'Online now' : 'Offline'}
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs value={infoTab} onChange={(_, v) => setInfoTab(v)} centered
        sx={{ minHeight: 38, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontSize: 13, fontWeight: 600, color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } },
          '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0' } }}>
        <Tab value="members" label={convo.type === 'team' ? 'Members' : 'About'} />
        <Tab value="pinned" label={`Pinned (${pinnedMessages.length})`} />
        <Tab value="search" label="Search" />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ minHeight: 200, maxHeight: 350, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        {infoTab === 'members' && (
          <Stack spacing={0}>
            {convo.participants.map((p) => (
              <Stack key={p.user_id} direction="row" alignItems="center" spacing={1.75}
                sx={{ px: 3, py: 1.5, transition: 'background-color 0.12s', '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' } }}>
                <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot"
                  sx={{ '& .MuiBadge-badge': { bgcolor: p.online ? '#059669' : '#94A3B8', border: '2px solid', borderColor: 'background.paper', width: 10, height: 10, borderRadius: 99 } }}>
                  <UserAvatar name={p.name} avatarUrl={p.avatar_url} size={38} sx={{ bgcolor: userColor(p.user_id) }} />
                </Badge>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{p.name}{p.user_id === myId ? ' (you)' : ''}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: p.online ? '#059669' : 'text.disabled', fontWeight: p.online ? 600 : 400 }}>{p.online ? 'Online' : 'Offline'}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        )}

        {infoTab === 'pinned' && (
          <Box sx={{ px: 3, py: 2 }}>
            {pinnedMessages.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <PushPinOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1, transform: 'rotate(45deg)' }} />
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>No pinned messages</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>Pin important messages to find them here</Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {pinnedMessages.map((m) => (
                  <Box key={m.id} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                      <PushPinOutlinedIcon sx={{ fontSize: 13, color: 'primary.main', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'primary.main' }}>{m.sender_name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{shortTime(new Date(m.created_at), formatDateShort)}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.5 }}>{m.text}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {infoTab === 'search' && (
          <Box sx={{ px: 3, py: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField placeholder="Search in this conversation..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()} autoFocus
                size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }} />
              <IconButton size="small" onClick={handleSearch} disabled={searching}
                sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 36, height: 36, borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}>
                <SearchIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
            {searching && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={22} /></Box>}
            {!searching && searchResults.length === 0 && searchQuery && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.75 }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No messages found for "{searchQuery}"</Typography>
              </Box>
            )}
            {!searching && searchResults.length > 0 && (
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 1.5, fontWeight: 600 }}>{searchResults.length} result{searchResults.length === 1 ? '' : 's'}</Typography>
            )}
            <Stack spacing={1}>
              {searchResults.map((r) => (
                <Box key={r.id} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', cursor: 'pointer', transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.35 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{r.sender_name}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>{shortTime(new Date(r.created_at), formatDateShort)}</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>{r.text}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

// ─── Message bubble ─────────────────────────────────────────────────────────

function MessageBubble({
  message: m, isMe, showAvatar, myId, participants, onReact, onReply, onPin, onDelete, onEdit, onViewFile,
}: {
  message: ChatMessage; isMe: boolean; showAvatar: boolean; myId: string
  participants: ChatParticipant[]
  onReact: (emoji: string) => void; onReply: () => void; onPin: () => void
  onDelete: () => void; onEdit: () => void; onViewFile: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const navigate = useNavigate()

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      <Stack direction="row" justifyContent={isMe ? 'flex-end' : 'flex-start'} alignItems="flex-end" spacing={1}
        sx={{ mb: (m.reactions?.length ?? 0) > 0 ? 1.5 : 0.75, position: 'relative' }}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>

        {!isMe && (
          <Box sx={{ width: 32, flexShrink: 0 }}>
            {showAvatar && <UserAvatar name={m.sender_name} avatarUrl={participants.find((p) => p.user_id === m.sender_id)?.avatar_url} size={32} sx={{ bgcolor: userColor(m.sender_id ?? '') }} />}
          </Box>
        )}

        <Box sx={{ maxWidth: '65%', position: 'relative', overflow: 'visible' }}>
          {showAvatar && !isMe && (
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.35, ml: 0.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{m.sender_name}</Typography>
              <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{timeOnly(new Date(m.created_at))}</Typography>
            </Stack>
          )}

          {/* Reply preview */}
          {m.reply_to && (
            <Box sx={{ ml: 0.5, mb: 0.5, pl: 1.5, py: 0.75, borderLeft: '3px solid', borderColor: 'primary.main',
              bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)', borderRadius: '0 8px 8px 0' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main' }}>{m.reply_to.sender_name}</Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>{m.reply_to.text}</Typography>
            </Box>
          )}

          {/* Pinned */}
          {m.pinned && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 0.5, mb: 0.35 }}>
              <PushPinOutlinedIcon sx={{ fontSize: 11, color: 'text.secondary', transform: 'rotate(45deg)' }} />
              <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 600 }}>Pinned</Typography>
            </Stack>
          )}

          {/* Bubble */}
          <Box sx={{
            px: m.file_url && m.file_type?.startsWith('image/') ? 0.5 : 2,
            py: m.file_url && m.file_type?.startsWith('image/') ? 0.5 : 1.25,
            borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            bgcolor: isMe ? 'primary.main' : (t) => t.palette.mode === 'light' ? '#F0F0F2' : 'rgba(255,255,255,0.08)',
            color: isMe ? 'primary.contrastText' : 'text.primary',
            overflow: 'hidden',
          }}>
            {/* File attachment */}
            {m.file_url && m.file_type?.startsWith('image/') ? (
              <ChatImage conversationId={m.conversation_id} messageId={m.id} onClick={onViewFile} />
            ) : m.file_url && m.file_type?.startsWith('audio/') ? (
              <ChatAudio conversationId={m.conversation_id} messageId={m.id} isMe={isMe} />
            ) : m.file_url ? (
              <Stack direction="row" alignItems="center" spacing={1.25}
                onClick={onViewFile}
                sx={{
                  px: 1.5, py: 1, cursor: 'pointer', borderRadius: 1.5,
                  bgcolor: isMe ? 'rgba(255,255,255,0.12)' : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                  mb: m.text ? 1 : 0,
                  '&:hover': { bgcolor: isMe ? 'rgba(255,255,255,0.2)' : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' },
                }}>
                <AttachFileOutlinedIcon sx={{ fontSize: 18 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{m.file_name ?? 'File'}</Typography>
                  {m.file_size && <Typography sx={{ fontSize: 10.5, opacity: 0.7 }}>{formatFileSize(m.file_size)}</Typography>}
                </Box>
              </Stack>
            ) : null}
            {m.text && (
              <Box sx={{
                px: m.file_url && m.file_type?.startsWith('image/') ? 1.5 : 0,
                pb: m.file_url && m.file_type?.startsWith('image/') ? 0.5 : 0,
              }}>
                <MentionText
                  text={m.text}
                  mentions={m.mentions ?? []}
                  inverse={isMe}
                  onTap={(mention) => navigateToMention(navigate, mention)}
                />
              </Box>
            )}
            {isMe && (
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.35} sx={{ mt: 0.35 }}>
                <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>{timeOnly(new Date(m.created_at))}</Typography>
                {m.status === 'read' && <DoneAllIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />}
                {m.status === 'delivered' && <DoneAllIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />}
                {m.status === 'sent' && <DoneIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />}
              </Stack>
            )}
          </Box>

          {/* Reactions */}
          {m.reactions && m.reactions.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, ml: isMe ? 'auto' : 0.5, mr: isMe ? 0.5 : 'auto', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
              {m.reactions.map((r, ri) => (
                <Box key={ri} onClick={() => onReact(r.emoji)} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.75, py: 0.25, borderRadius: 99,
                  bgcolor: r.user_ids.includes(myId) ? primary.soft : 'action.hover',
                  border: '1px solid', borderColor: r.user_ids.includes(myId) ? 'primary.main' : 'divider',
                  cursor: 'pointer', fontSize: 13, transition: 'transform 120ms', '&:hover': { transform: 'scale(1.1)' },
                }}>
                  <span>{r.emoji}</span>
                  {r.user_ids.length > 1 && <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.secondary' }}>{r.user_ids.length}</Typography>}
                </Box>
              ))}
            </Stack>
          )}

          {/* Hover actions */}
          <AnimatePresence>
            {hovered && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.12 }}
                style={{ position: 'absolute', top: -36, ...(isMe ? { right: 0 } : { left: 0 }), zIndex: 3 }}>
                <Stack direction="row" spacing={0.25}
                  sx={{ bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 12px -4px rgba(0,0,0,0.15)', px: 0.5, py: 0.25 }}>
                  {QUICK_EMOJIS.slice(0, 4).map((e) => (
                    <IconButton key={e} size="small" onClick={() => onReact(e)}
                      sx={{ width: 28, height: 28, fontSize: 14, '&:hover': { transform: 'scale(1.2)' }, transition: 'transform 100ms' }}>
                      {e}
                    </IconButton>
                  ))}
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                  <Tooltip title="Reply" arrow><IconButton size="small" onClick={onReply} sx={{ width: 28, height: 28, color: 'text.secondary' }}><ReplyOutlinedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                  <Tooltip title={m.pinned ? 'Unpin' : 'Pin'} arrow><IconButton size="small" onClick={onPin} sx={{ width: 28, height: 28, color: m.pinned ? 'primary.main' : 'text.secondary' }}><PushPinOutlinedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                  {isMe && (
                    <>
                      {m.text && <Tooltip title="Edit" arrow><IconButton size="small" onClick={onEdit} sx={{ width: 28, height: 28, color: 'text.secondary' }}><EditOutlinedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>}
                      <Tooltip title="Delete" arrow><IconButton size="small" onClick={onDelete} sx={{ width: 28, height: 28, color: 'text.secondary', '&:hover': { color: 'error.main' } }}><DeleteOutlineIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                    </>
                  )}
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Stack>
    </motion.div>
  )
}

// ─── New conversation dialog ─────────────────────────────────────────────

function NewConversationDialog({ onClose, myId, onCreated }: { onClose: () => void; myId: string; onCreated: (id: string) => void }) {
  const [type, setType] = useState<'direct' | 'team'>('direct')
  const [name, setName] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.users.list(), staleTime: 60_000 })
  const users = Array.isArray(usersQ.data) ? usersQ.data.filter((u: any) => u.id !== myId) : []

  const createM = useMutation({
    mutationFn: () => api.chat.createConversation({
      type,
      name: type === 'team' ? name.trim() : undefined,
      participant_ids: [...selectedIds, myId],
    }),
    onSuccess: (conv) => { onCreated(conv.id); onClose() },
  })

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>New conversation</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Select value={type} onChange={(e) => setType(e.target.value as any)} size="small" fullWidth>
            <MenuItem value="direct">Direct message</MenuItem>
            <MenuItem value="team">Group chat</MenuItem>
          </Select>
          {type === 'team' && (
            <TextField label="Group name" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth placeholder="e.g. HVAC Crew" />
          )}
          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
              {type === 'direct' ? 'Select a person' : 'Add members'}
            </Typography>
            <Stack spacing={0.5} sx={{ maxHeight: 200, overflowY: 'auto', scrollbarWidth: 'thin' }}>
              {users.map((u: any) => {
                const selected = selectedIds.includes(u.id)
                return (
                  <Stack key={u.id} direction="row" alignItems="center" spacing={1.5}
                    onClick={() => {
                      if (type === 'direct') setSelectedIds([u.id])
                      else setSelectedIds((prev) => selected ? prev.filter((id) => id !== u.id) : [...prev, u.id])
                    }}
                    sx={{
                      px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer',
                      bgcolor: selected ? (t) => `${t.palette.primary.main}12` : 'transparent',
                      border: '1px solid', borderColor: selected ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' },
                    }}>
                    <UserAvatar name={u.name} avatarUrl={(u as any).avatar_url} size={32} sx={{ bgcolor: userColor(u.id) }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{u.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{u.email}</Typography>
                    </Box>
                  </Stack>
                )
              })}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => createM.mutate()}
          disabled={selectedIds.length === 0 || (type === 'team' && !name.trim()) || createM.isPending}
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {createM.isPending ? 'Creating...' : 'Start chat'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function shortTime(d: Date, formatDateShort: (d: Date | string) => string): string {
  const diff = Date.now() - d.getTime()
  if (diff < 60_000) return 'now'
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  return formatDateShort(d)
}

function ChatImage({ conversationId, messageId, onClick }: { conversationId: string; messageId: string; onClick: () => void }) {
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    let revoke: string | null = null
    api.chat.viewFile(conversationId, messageId)
      .then((res) => {
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data])
        const url = URL.createObjectURL(blob)
        revoke = url
        setSrc(url)
      })
      .catch(() => {})
    return () => { if (revoke) URL.revokeObjectURL(revoke) }
  }, [conversationId, messageId])

  if (!src) return <Box sx={{ width: 200, height: 140, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={20} /></Box>

  return (
    <Box
      component="img" src={src} onClick={onClick}
      sx={{ display: 'block', maxWidth: 280, maxHeight: 240, borderRadius: 2, cursor: 'pointer', objectFit: 'cover' }}
    />
  )
}

function ChatAudio({ conversationId, messageId, isMe }: { conversationId: string; messageId: string; isMe: boolean }) {
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    let revoke: string | null = null
    api.chat.viewFile(conversationId, messageId)
      .then((res) => {
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data])
        const url = URL.createObjectURL(blob)
        revoke = url
        setSrc(url)
      })
      .catch(() => {})
    return () => { if (revoke) URL.revokeObjectURL(revoke) }
  }, [conversationId, messageId])

  if (!src) return <Box sx={{ width: 220, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={16} /></Box>

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 220 }}>
      <MicNoneOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
      <Box component="audio" controls src={src} preload="metadata"
        sx={{
          height: 34, flex: 1, minWidth: 0,
          '&::-webkit-media-controls-panel': { bgcolor: 'transparent' },
          filter: isMe ? 'invert(1) brightness(2)' : 'none',
        }} />
    </Stack>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function timeOnly(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
}

function dayLabel(d: Date): string {
  const today = new Date()
  if (d.toDateString() === today.toDateString()) return 'Today'
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
}
